// localStorage / sessionStorage key names.
//
// IMPORTANT: every key in this file is LIVE in user browsers. Renaming
// any one of them orphans state for returning users (lost onboarding
// progress, lost API-base override, lost saved scenarios). Add new
// keys here; do NOT rename existing ones without a migration plan.
//
// Keys are grouped by the surface that writes them. The shape is
// documented inline so future writers don't have to grep callsites
// to learn what's in each slot.

// Auth + session
export const STORAGE_AUTH = {
  /** User snapshot cached for the role-gated routes (lib/auth.ts). */
  user: "propwise.auth_user.v1",
  /** Long-lived user identifier persisted across sessions (lib/user.ts). */
  userId: "propwise.user_id.v1",
} as const;

// Custom DOM event names dispatched on window. Listed alongside the
// storage keys because they're effectively channel names that also
// pin a user-visible string.
export const STORAGE_EVENTS = {
  authChanged: "propwise.auth_changed",
  conversationCreated: "propwise:conversation-created",
  workspaceRefresh: "propwise:workspace-refresh",
} as const;

// API base override. lib/config.ts reads this for runtime BE switching
// in DevTools without rebuilding.
export const STORAGE_API = {
  base: "propwise.api",
} as const;

// Multi-stage onboarding state machine (lib/onboarding.ts). The keys
// here drive ChatWelcomeModal, WorkspacePrefsBanner, WorkspaceTour.
export const STORAGE_ONBOARDING = {
  workspaceWelcomeDismissed: "propwise.workspaceWelcome.dismissed",
  workspaceTourCompleted: "propwise.workspaceTour.completed",
  chatWelcomeDismissed: "propwise.chatWelcome.dismissed",
  prefsSeedFailed: "propwise.workspacePrefsSeed.failed",
  prefsSkipped: "propwise.workspacePrefs.skipped",
  proOnboarded: "propwise.pro.onboarded.v1",
} as const;

// Chat scoping. CONV_KEY_PREFIX and PENDING_KEY_PREFIX get suffixed
// per-conversation/per-page (useChat.ts).
//
// The chat-welcome-modal dismissal flag belongs to onboarding (and lives
// in STORAGE_ONBOARDING.chatWelcomeDismissed) — same physical localStorage
// key, single named owner, so the two surfaces can't drift.
export const STORAGE_CHAT = {
  conversationIdPrefix: "propwise.conversation_id",
  pendingStreamPrefix: "propwise.pending_stream",
} as const;

// Lead / CRM state used by FE-only mock surfaces.
export const STORAGE_LEADS = {
  leadSubmitted: "propwise.lead_submitted",
  /** Sibling of leadSubmitted scoped to the rent flow — a buy-side lead
   *  doesn't suppress the rent CTA (and vice versa). */
  leadSubmittedRent: "propwise.lead_submitted_rent",
  crmLeads: "propwise.crm.leads.v1",
} as const;

// Per-page saved state.
export const STORAGE_SAVED = {
  consumerRent: "propwise.saved.consumer_rent.v1",
} as const;

// UI preferences.
export const STORAGE_UI = {
  theme: "propwise.theme",
  language: "propwise.lang",
  consumerSidebarCollapsed: "pw:consumer-sidebar-collapsed",
  agentSidebarCollapsed: "pw:agent-sidebar-collapsed",
} as const;

// Per-feature freemium usage counters. The full key is built per-feature
// at runtime: `${freemiumUsagePrefix}${feature}.${monthKey()}`.
export const STORAGE_FREEMIUM = {
  usagePrefix: "propwise.usage.",
} as const;
